import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import type {
  LevelData,
  InstancesData,
  ObjectInstance,
  EntityInstance,
} from '@shared/types/LevelTypes';

// Unique ID for editor objects
let nextId = 1;
export function generateId(): string {
  return `obj_${nextId++}`;
}

// Editor object with unique ID
export interface EditorObject {
  id: string;
  layerName: string;
  data: ObjectInstance;
}

export type EditorEntityData = EntityInstance & {
  id?: string;
  originX?: number;
  originY?: number;
};

export interface EditorEntity {
  id: string;
  data: EditorEntityData;
}

export interface PaletteLayoutPayload {
  type: string;
  layerName?: string;
  width?: number;
  height?: number;
  originX?: number;
  originY?: number;
  angle?: number;
}

export interface PaletteEntityPayload {
  entityId: string;
  texture?: string;
  width?: number;
  height?: number;
  originX?: number;
  originY?: number;
  properties?: Record<string, unknown>;
}

export interface PaletteInspectorSelection {
  kind: 'layout' | 'entity';
  type: string;
  width?: number;
  height?: number;
  animation?: string;
}

export type EditorMode = 'layouts' | 'entities';
export type EntityPaletteScope = 'level' | 'all';
export type Tool = 'select' | 'move' | 'add';

interface HistoryState {
  objects: EditorObject[];
  entities: EditorEntity[];
  levelData: LevelData | null;
  instancesData: InstancesData | null;
  editorLayers: string[];
  layerVisibility: Record<string, boolean>;
  activeLayer: string | null;
  entityLayerAssignments: Record<string, string>;
}

interface EditorState {
  // Data
  levelId: string | null;
  levelData: LevelData | null;
  instancesData: InstancesData | null;

  // Editor objects (with IDs)
  objects: EditorObject[];
  entities: EditorEntity[];

  levelCache: Record<string, HistoryState>;

  // Mode
  mode: EditorMode;
  entityPaletteScope: EntityPaletteScope;
  tool: Tool;

  // Selection
  selectedIds: string[];
  paletteInspectorSelection: PaletteInspectorSelection | null;

  // Layers
  editorLayers: string[];
  layerVisibility: Record<string, boolean>;
  activeLayer: string | null;
  entityLayerAssignments: Record<string, string>;

  // History
  history: HistoryState[];
  historyIndex: number;

  // Zoom/Pan
  zoom: number;
  panX: number;
  panY: number;

  // Loading
  isLoading: boolean;
  error: string | null;

  // Actions
  loadLevel: (levelId: string) => Promise<void>;
  setMode: (mode: EditorMode) => void;
  setEntityPaletteScope: (scope: EntityPaletteScope) => void;
  setTool: (tool: Tool) => void;

  // Selection
  selectObject: (id: string, addToSelection?: boolean) => void;
  selectMultiple: (ids: string[]) => void;
  clearSelection: () => void;
  setPaletteInspectorSelection: (selection: PaletteInspectorSelection | null) => void;

  // Object manipulation
  moveSelected: (dx: number, dy: number) => void;
  updateObjectProperty: (id: string, prop: string, value: unknown, skipHistory?: boolean) => void;
  updateEntityInstanceProperty: (
    id: string,
    prop: string,
    value: unknown,
    skipHistory?: boolean
  ) => void;
  setObjectPosition: (id: string, x: number, y: number, skipHistory?: boolean) => void;
  deleteSelected: () => void;
  addLayoutFromPalette: (payload: PaletteLayoutPayload, x: number, y: number) => void;
  addEntityFromPalette: (payload: PaletteEntityPayload, x: number, y: number) => void;

  // Layers
  toggleLayerVisibility: (layerName: string) => void;
  setActiveLayer: (layerName: string) => void;
  createEditorLayer: (layerName: string) => void;
  setEntityLayer: (entityId: string, layerName: string) => void;

  // History
  undo: () => void;
  redo: () => void;
  pushHistory: () => void;

  // Viewport
  setZoom: (zoom: number) => void;
  setPan: (x: number, y: number) => void;

  // Save
  save: () => void;
  exportJSON: () => void;
}

export const useEditorStore = create<EditorState>()(
  immer((set, get) => ({
    // Initial state
    levelId: null,
    levelData: null,
    instancesData: null,
    objects: [],
    entities: [],
    levelCache: {},
    mode: 'layouts',
    entityPaletteScope: 'level',
    tool: 'select',
    selectedIds: [],
    paletteInspectorSelection: null,
    editorLayers: [],
    layerVisibility: {},
    activeLayer: null,
    entityLayerAssignments: {},
    history: [],
    historyIndex: -1,
    zoom: 1,
    panX: 0,
    panY: 0,
    isLoading: false,
    error: null,

    // Load level from JSON files
    loadLevel: async (levelId: string) => {
      const currentLevel = get().levelId;

      if (currentLevel) {
        const {
          objects,
          entities,
          levelData,
          instancesData,
          editorLayers,
          layerVisibility,
          activeLayer,
          entityLayerAssignments,
        } = get();

        set((state) => {
          state.levelCache[currentLevel] = {
            objects: JSON.parse(JSON.stringify(objects)),
            entities: JSON.parse(JSON.stringify(entities)),
            levelData: JSON.parse(JSON.stringify(levelData)),
            instancesData: JSON.parse(JSON.stringify(instancesData)),
            editorLayers: JSON.parse(JSON.stringify(editorLayers)),
            layerVisibility: JSON.parse(JSON.stringify(layerVisibility)),
            activeLayer,
            entityLayerAssignments: JSON.parse(JSON.stringify(entityLayerAssignments)),
          };
        });
      }
      const cached = get().levelCache[levelId];

      if (cached) {
        set((state) => {
          state.levelId = levelId;
          state.objects = JSON.parse(JSON.stringify(cached.objects));
          state.entities = JSON.parse(JSON.stringify(cached.entities));
          state.levelData = JSON.parse(JSON.stringify(cached.levelData));
          state.instancesData = JSON.parse(JSON.stringify(cached.instancesData));
          state.editorLayers = JSON.parse(JSON.stringify(cached.editorLayers));
          state.layerVisibility = JSON.parse(JSON.stringify(cached.layerVisibility));
          state.activeLayer = cached.activeLayer;
          state.entityLayerAssignments = JSON.parse(JSON.stringify(cached.entityLayerAssignments));
          state.selectedIds = [];
          state.history = [];
          state.historyIndex = -1;
        });

        get().pushHistory();
        return;
      }

      set((state) => {
        state.isLoading = true;
        state.error = null;
      });

      try {
        console.log('[Editor] Cargando nivel:', levelId);

        const parseJsonSafe = async <T>(response: Response): Promise<T | null> => {
          if (!response.ok) return null;

          const raw = await response.text();
          const trimmed = raw.trim();

          // Vite dev fallback can return index.html with 200 for missing files.
          if (!trimmed || trimmed.startsWith('<!DOCTYPE') || trimmed.startsWith('<html')) {
            return null;
          }

          try {
            return JSON.parse(trimmed) as T;
          } catch {
            return null;
          }
        };

        // Load base and overrides in parallel
        const LEVELS_PATH = '/assets/levels';
        const [baseRes, entitiesRes, layoutOverrideRes, entitiesOverrideRes] = await Promise.all([
          fetch(`${LEVELS_PATH}/${levelId}-layout-c3data.json`),
          fetch(`${LEVELS_PATH}/${levelId}-entities-c3data.json`),
          fetch(`${LEVELS_PATH}/${levelId}-layout-overrides.json`),
          fetch(`${LEVELS_PATH}/${levelId}-entities-overrides.json`),
        ]);

        if (!baseRes.ok) {
          throw new Error(`No se pudo cargar la base C3: ${baseRes.status}`);
        }

        const baseData = await parseJsonSafe<LevelData>(baseRes);
        if (!baseData) {
          throw new Error('No se pudo parsear layout-c3data.json del nivel.');
        }

        let baseEntities: InstancesData = { entities: [] };
        const entitiesParsed = await parseJsonSafe<InstancesData>(entitiesRes);
        if (entitiesParsed) {
          baseEntities = entitiesParsed;
        }

        let layoutOverrides: Partial<LevelData> = {};
        const layoutOverridesParsed = await parseJsonSafe<Partial<LevelData>>(layoutOverrideRes);
        if (layoutOverridesParsed) {
          layoutOverrides = layoutOverridesParsed;
        }

        let entitiesOverrides: InstancesData = { entities: [] };
        const rawOverride = await parseJsonSafe<any>(entitiesOverrideRes);
        if (rawOverride) {
          const overrideList = rawOverride?.entities ?? rawOverride?.additions?.entities ?? [];
          entitiesOverrides = {
            entities: (overrideList as EntityInstance[]).filter(
              (e) => e && typeof e === 'object' && 'entityId' in e
            ),
          };
        }

        const objects: EditorObject[] = [];
        const layerVisibility: Record<string, boolean> = {};
        const baseLayers = baseData.layers;
        const overrideLayers = layoutOverrides.layers ?? [];

        const overrideObjMap = new Map<string, ObjectInstance>();
        for (const layer of overrideLayers) {
          if (!layer.objects) continue;
          for (const obj of layer.objects) {
            overrideObjMap.set(`${layer.name}-${obj.type}-${obj.x}-${obj.y}`, obj);
          }
        }

        for (const layer of baseLayers) {
          layerVisibility[layer.name] = layer.visible;
          if (!layer.objects) continue;

          for (const obj of layer.objects) {
            const overrideObj = overrideObjMap.get(`${layer.name}-${obj.type}-${obj.x}-${obj.y}`);
            objects.push({
              id: generateId(),
              layerName: layer.name,
              data: overrideObj ? { ...obj, ...overrideObj } : { ...obj },
            });
          }
        }

        for (const layer of overrideLayers) {
          if (!layer.objects) continue;
          for (const obj of layer.objects) {
            if (
              !objects.find(
                (o) =>
                  o.layerName === layer.name &&
                  o.data.type === obj.type &&
                  o.data.x === obj.x &&
                  o.data.y === obj.y
              )
            ) {
              objects.push({
                id: generateId(),
                layerName: layer.name,
                data: { ...obj },
              });
            }
          }
        }

        const getEntityStableKey = (entity: EntityInstance) => {
          const explicitId = (entity as { id?: string }).id;
          return explicitId ? `id:${explicitId}` : `pos:${entity.entityId}-${entity.x}-${entity.y}`;
        };

        const baseEntityMap = new Map<string, EntityInstance>();
        for (const e of baseEntities.entities) {
          baseEntityMap.set(getEntityStableKey(e), e);
        }

        const overrideEntityMap = new Map<string, EntityInstance>();
        for (const e of entitiesOverrides.entities) {
          overrideEntityMap.set(getEntityStableKey(e), e);
        }

        const entities: EditorEntity[] = [];
        const entityLayerAssignments: Record<string, string> = {};

        for (const [key, baseE] of baseEntityMap.entries()) {
          const overrideE = overrideEntityMap.get(key);
          const newId = generateId();

          entities.push({
            id: newId,
            data: overrideE ? { ...baseE, ...overrideE } : { ...baseE },
          });

          entityLayerAssignments[newId] = baseData.layers[0]?.name ?? 'default';
        }

        for (const [key, overrideE] of overrideEntityMap.entries()) {
          if (!baseEntityMap.has(key)) {
            const newId = generateId();

            entities.push({
              id: newId,
              data: { ...overrideE },
            });

            entityLayerAssignments[newId] = baseData.layers[0]?.name ?? 'default';
          }
        }

        set((state) => {
          state.levelId = levelId;
          state.levelData = baseData;
          state.instancesData = baseEntities;
          state.objects = objects;
          state.entities = entities;
          state.editorLayers = baseData.layers.map((layer) => layer.name);
          state.layerVisibility = layerVisibility;
          state.activeLayer = baseData.layers[0]?.name ?? null;
          state.entityLayerAssignments = entityLayerAssignments;
          state.selectedIds = [];
          state.history = [];
          state.historyIndex = -1;
          state.zoom = 0.1;
          state.panX = 0;
          state.panY = 0;
        });

        get().pushHistory();
      } catch (error) {
        set((state) => {
          state.error = error instanceof Error ? error.message : 'Unknown error';
        });

        console.error('[Editor] Error:', error);
      } finally {
        set((state) => {
          state.isLoading = false;
        });
      }
    },

    setMode: (mode: EditorMode) => {
      set((state) => {
        state.mode = mode;
        if (mode === 'layouts') {
          state.entityPaletteScope = 'level';
        }
        state.selectedIds = [];
      });
    },

    setEntityPaletteScope: (scope: EntityPaletteScope) => {
      set((state) => {
        state.entityPaletteScope = scope;
        state.mode = 'entities';
        state.selectedIds = [];
      });
    },

    setTool: (tool: Tool) => {
      set((state) => {
        state.tool = tool;
      });
    },

    selectObject: (id: string, addToSelection = false) => {
      set((state) => {
        if (addToSelection) {
          const idx = state.selectedIds.indexOf(id);
          if (idx >= 0) {
            state.selectedIds.splice(idx, 1);
          } else {
            state.selectedIds.push(id);
          }
        } else {
          state.selectedIds = [id];
        }
      });
    },

    selectMultiple: (ids: string[]) => {
      set((state) => {
        state.selectedIds = ids;
      });
    },

    clearSelection: () => {
      set((state) => {
        state.selectedIds = [];
      });
    },

    setPaletteInspectorSelection: (selection: PaletteInspectorSelection | null) => {
      set((state) => {
        state.paletteInspectorSelection = selection;
      });
    },

    moveSelected: (dx: number, dy: number) => {
      const { selectedIds, mode } = get();
      if (selectedIds.length === 0) return;

      set((state) => {
        if (mode === 'layouts') {
          for (const obj of state.objects) {
            if (selectedIds.includes(obj.id)) {
              obj.data.x += dx;
              obj.data.y += dy;
            }
          }
        } else {
          for (const entity of state.entities) {
            if (selectedIds.includes(entity.id)) {
              entity.data.x += dx;
              entity.data.y += dy;
            }
          }
        }
      });
    },

    updateObjectProperty: (id: string, prop: string, value: unknown, skipHistory = false) => {
      const { mode, levelData, instancesData } = get();

      set((state) => {
        if (mode === 'layouts') {
          const obj = state.objects.find((o) => o.id === id);
          if (obj) {
            // Only store the diff from base
            const baseLayer = levelData?.layers.find((l) => l.name === obj.layerName);
            const baseObj = baseLayer?.objects?.find(
              (o) => o.type === obj.data.type && o.x === obj.data.x && o.y === obj.data.y
            );
            if (baseObj) {
              // Only update override if value differs from base
              if ((baseObj as any)[prop] !== value) {
                (obj.data as Record<string, unknown>)[prop] = value;
              } else {
                // If value matches base, remove from override (reset to base)
                delete (obj.data as Record<string, unknown>)[prop];
              }
            } else {
              // New object (not in base), just update
              (obj.data as Record<string, unknown>)[prop] = value;
            }
          }
        } else {
          const entity = state.entities.find((e) => e.id === id);
          if (entity) {
            const baseEntity = instancesData?.entities.find(
              (e0) =>
                e0.entityId === entity.data.entityId &&
                e0.x === entity.data.x &&
                e0.y === entity.data.y
            );
            if (baseEntity) {
              if ((baseEntity as any)[prop] !== value) {
                (entity.data as Record<string, unknown>)[prop] = value;
              } else {
                delete (entity.data as Record<string, unknown>)[prop];
              }
            } else {
              (entity.data as Record<string, unknown>)[prop] = value;
            }
          }
        }
      });

      if (!skipHistory) {
        get().pushHistory();
      }
    },

    updateEntityInstanceProperty: (id: string, prop: string, value: unknown, skipHistory = false) => {
      set((state) => {
        const entity = state.entities.find((item) => item.id === id);
        if (!entity) return;

        const currentProperties =
          entity.data.properties && typeof entity.data.properties === 'object'
            ? entity.data.properties
            : {};

        entity.data.properties = {
          ...currentProperties,
          [prop]: value,
        };
      });

      if (!skipHistory) {
        get().pushHistory();
      }
    },

    setObjectPosition: (id: string, x: number, y: number, skipHistory = false) => {
      set((state) => {
        const obj = state.objects.find((item) => item.id === id);
        if (obj) {
          obj.data.x = x;
          obj.data.y = y;
          return;
        }

        const entity = state.entities.find((item) => item.id === id);
        if (entity) {
          entity.data.x = x;
          entity.data.y = y;
        }
      });

      if (!skipHistory) {
        get().pushHistory();
      }
    },

    deleteSelected: () => {
      const { selectedIds, mode } = get();
      if (selectedIds.length === 0) return;

      set((state) => {
        if (mode === 'layouts') {
          state.objects = state.objects.filter((o) => !selectedIds.includes(o.id));
        } else {
          state.entities = state.entities.filter((e) => !selectedIds.includes(e.id));
          for (const id of selectedIds) {
            delete state.entityLayerAssignments[id];
          }
        }
        state.selectedIds = [];
      });

      get().pushHistory();
    },

    addLayoutFromPalette: (payload: PaletteLayoutPayload, x: number, y: number) => {
      const { activeLayer, levelData, layerVisibility } = get();
      const baseLayerNames = new Set((levelData?.layers ?? []).map((layer) => layer.name));
      const visibleBaseLayer = levelData?.layers.find(
        (layer) => layerVisibility[layer.name] ?? layer.visible
      )?.name;
      const defaultLayer = levelData?.layers[0]?.name;
      const sourceLayer =
        payload.layerName &&
        baseLayerNames.has(payload.layerName) &&
        layerVisibility[payload.layerName] !== false
          ? payload.layerName
          : null;
      const activeVisibleLayer =
        activeLayer &&
        activeLayer !== defaultLayer &&
        baseLayerNames.has(activeLayer) &&
        layerVisibility[activeLayer] !== false
          ? activeLayer
          : null;
      const targetLayer = activeVisibleLayer ?? sourceLayer ?? visibleBaseLayer ?? defaultLayer;
      if (!targetLayer) return;

      const newId = generateId();
      const width = payload.width ?? 64;
      const height = payload.height ?? 64;
      const originX = payload.originX ?? 0;
      const originY = payload.originY ?? 0;
      const newData: ObjectInstance = {
        type: payload.type,
        x: x + width * originX - width / 2,
        y: y + height * originY - height / 2,
        width,
        height,
      };
      if (payload.originX !== undefined) newData.originX = payload.originX;
      if (payload.originY !== undefined) newData.originY = payload.originY;
      if (payload.angle !== undefined) newData.angle = payload.angle;

      set((state) => {
        state.objects.push({
          id: newId,
          layerName: targetLayer,
          data: newData,
        });
        state.selectedIds = [newId];
      });

      get().pushHistory();
    },

    addEntityFromPalette: (payload: PaletteEntityPayload, x: number, y: number) => {
      const newId = generateId();
      const editorEntityTag = `#EDITOR_${newId}`;
      const width = payload.width ?? 64;
      const height = payload.height ?? 64;
      const originX = payload.originX ?? 0.5;
      const originY = payload.originY ?? 1;
      const newEntityData: EditorEntityData = {
        id: editorEntityTag,
        entityId: payload.entityId,
        texture: payload.texture ?? payload.entityId,
        x: x + width * originX - width / 2,
        y: y + height * originY - height / 2,
        width,
        height,
      };
      if (payload.properties !== undefined) newEntityData.properties = payload.properties;
      if (payload.originX !== undefined) newEntityData.originX = payload.originX;
      if (payload.originY !== undefined) newEntityData.originY = payload.originY;

      set((state) => {
        const visibleLayer = state.editorLayers.find(
          (layerName) => state.layerVisibility[layerName] !== false
        );
        const fallbackLayer =
          state.activeLayer && state.layerVisibility[state.activeLayer] !== false
            ? state.activeLayer
            : (visibleLayer ?? state.editorLayers[0] ?? 'default');
        state.entities.push({
          id: newId,
          data: newEntityData,
        });
        state.entityLayerAssignments[newId] = fallbackLayer;
        state.selectedIds = [newId];
      });

      get().pushHistory();
    },

    toggleLayerVisibility: (layerName: string) => {
      set((state) => {
        state.layerVisibility[layerName] = !state.layerVisibility[layerName];
      });
    },

    setActiveLayer: (layerName: string) => {
      set((state) => {
        state.activeLayer = layerName;
      });
    },

    createEditorLayer: (layerName: string) => {
      const trimmed = layerName.trim();
      if (!trimmed) return;

      set((state) => {
        if (state.editorLayers.includes(trimmed)) return;
        state.editorLayers.push(trimmed);
        state.layerVisibility[trimmed] = true;
        if (!state.activeLayer) {
          state.activeLayer = trimmed;
        }
      });
    },

    setEntityLayer: (entityId: string, layerName: string) => {
      set((state) => {
        if (!state.editorLayers.includes(layerName)) return;
        const exists = state.entities.some((entity) => entity.id === entityId);
        if (!exists) return;
        state.entityLayerAssignments[entityId] = layerName;
      });
    },

    undo: () => {
      const { historyIndex, history } = get();
      if (historyIndex <= 0) return;

      const prevState = history[historyIndex - 1];
      if (!prevState) return;

      set((state) => {
        state.objects = JSON.parse(JSON.stringify(prevState.objects));
        state.entities = JSON.parse(JSON.stringify(prevState.entities));
        state.levelData = JSON.parse(JSON.stringify(prevState.levelData));
        state.instancesData = JSON.parse(JSON.stringify(prevState.instancesData));
        state.editorLayers = JSON.parse(JSON.stringify(prevState.editorLayers));
        state.layerVisibility = JSON.parse(JSON.stringify(prevState.layerVisibility));
        state.activeLayer = prevState.activeLayer;
        state.entityLayerAssignments = JSON.parse(JSON.stringify(prevState.entityLayerAssignments));
        state.historyIndex = historyIndex - 1;
        state.selectedIds = [];
      });
    },

    redo: () => {
      const { historyIndex, history } = get();
      if (historyIndex >= history.length - 1) return;

      const nextState = history[historyIndex + 1];
      if (!nextState) return;

      // Deep clone to avoid Immer issues
      const restoredObjects = JSON.parse(JSON.stringify(nextState.objects));
      const restoredEntities = JSON.parse(JSON.stringify(nextState.entities));

      set((state) => {
        state.objects = restoredObjects;
        state.entities = restoredEntities;
        state.historyIndex = historyIndex + 1;
        state.selectedIds = [];
      });
    },

    pushHistory: () => {
      const {
        objects,
        entities,
        levelData,
        instancesData,
        editorLayers,
        layerVisibility,
        activeLayer,
        entityLayerAssignments,
        history,
        historyIndex,
      } = get();

      const snapshot: HistoryState = {
        objects: JSON.parse(JSON.stringify(objects)),
        entities: JSON.parse(JSON.stringify(entities)),
        levelData: JSON.parse(JSON.stringify(levelData)),
        instancesData: JSON.parse(JSON.stringify(instancesData)),
        editorLayers: JSON.parse(JSON.stringify(editorLayers)),
        layerVisibility: JSON.parse(JSON.stringify(layerVisibility)),
        activeLayer,
        entityLayerAssignments: JSON.parse(JSON.stringify(entityLayerAssignments)),
      };

      set((state) => {
        state.history = history.slice(0, historyIndex + 1);
        state.history.push(snapshot);

        if (state.history.length > 50) {
          state.history.shift();
        }

        state.historyIndex = state.history.length - 1;
      });
    },

    setZoom: (zoom: number) => {
      set((state) => {
        state.zoom = Math.max(0.05, Math.min(2, zoom));
      });
    },

    setPan: (x: number, y: number) => {
      set((state) => {
        state.panX = x;
        state.panY = y;
      });
    },

    save: () => {
      console.log('[Editor] Guardar: se llamó a save()');
      get().exportJSON();
    },

    exportJSON: () => {
      console.log('[Editor] Exportar: se llamó a exportJSON()');
      const { levelId, objects, entities, levelData, instancesData } = get();
      if (!levelId) {
        console.warn('[Editor] Exportar: No hay levelId, no se puede exportar');
        return;
      }

      // Export layout overrides: only diffs from base
      const layoutExport = { layers: [] as any[] };
      if (levelData) {
        for (const baseLayer of levelData.layers) {
          const overrideObjs: any[] = [];
          for (const obj of objects.filter((o) => o.layerName === baseLayer.name)) {
            // Only export if differs from base
            const baseObj = baseLayer.objects?.find(
              (o0) => o0.type === obj.data.type && o0.x === obj.data.x && o0.y === obj.data.y
            );
            if (baseObj) {
              // Export only changed props
              const diff: any = { type: obj.data.type, x: obj.data.x, y: obj.data.y };
              let hasDiff = false;
              for (const key in obj.data) {
                if ((obj.data as any)[key] !== (baseObj as any)[key]) {
                  diff[key] = (obj.data as any)[key];
                  hasDiff = true;
                }
              }
              if (hasDiff) overrideObjs.push(diff);
            } else {
              // New object, export full
              overrideObjs.push(obj.data);
            }
          }
          if (overrideObjs.length > 0) {
            layoutExport.layers.push({
              name: baseLayer.name,
              objects: overrideObjs,
              visible: baseLayer.visible,
            });
          }
        }
      }
      const blobLayout = new Blob([JSON.stringify(layoutExport, null, 2)], {
        type: 'application/json',
      });
      const urlLayout = URL.createObjectURL(blobLayout);
      const aLayout = document.createElement('a');
      aLayout.href = urlLayout;
      aLayout.download = `${levelId}-layout-overrides.json`;
      aLayout.click();
      URL.revokeObjectURL(urlLayout);

      // Export entity overrides using level-specific additions format used by the game runtime
      const entitiesExport = {
        additions: {
          entities: [] as any[],
        },
      };
      const baseEntityIds = new Set(
        (instancesData?.entities ?? [])
          .map((e) => (e as { id?: string }).id)
          .filter((id): id is string => !!id)
      );
      const baseEntitiesById = new Map(
        (instancesData?.entities ?? [])
          .map((entity) => [(entity as { id?: string }).id, entity] as const)
          .filter((entry): entry is [string, EntityInstance] => !!entry[0])
      );
      for (const entity of entities) {
        const entityId = (entity.data as { id?: string }).id;
        const isFromBase = !!entityId && baseEntityIds.has(entityId);
        const baseEntity = entityId ? baseEntitiesById.get(entityId) : undefined;
        const hasDiff = baseEntity
          ? JSON.stringify(entity.data) !== JSON.stringify(baseEntity)
          : true;

        if (!isFromBase || hasDiff) {
          entitiesExport.additions.entities.push(entity.data);
        }
      }
      const blobEntities = new Blob([JSON.stringify(entitiesExport, null, 2)], {
        type: 'application/json',
      });
      const urlEntities = URL.createObjectURL(blobEntities);
      const aEntities = document.createElement('a');
      aEntities.href = urlEntities;
      aEntities.download = `${levelId}-entities-overrides.json`;
      aEntities.click();
      URL.revokeObjectURL(urlEntities);
    },
  }))
);
